import { useState, useMemo } from 'react';
import { Loader, Checkbox, Button, Text, ScrollArea, ActionIcon, Tooltip, Divider } from '@mantine/core';
import { api } from '~/trpc-extra/api';
import { modals } from '@mantine/modals';
import { IconChevronLeft, IconCopy, IconX } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { CopyStepTestInfo } from './CopyStepTestInfo';
import type { TestContentWithSimpleSteps } from '~/server/api/routers/test';
import Step from '../Step';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/tables/common/BaseTable';

export function CopyStepTestView({
  copyIntoTestContentId,
  copyFromTestContent,
  insertAfterStepOrder,
}: {
  copyIntoTestContentId: string;
  copyFromTestContent: TestContentWithSimpleSteps;
  insertAfterStepOrder: number;
}) {
  const trpcUtils = api.useUtils();
  const { data: testContent, isLoading: isTestContentLoading } = api.testContent.get.useQuery({
    id: copyFromTestContent.id,
  });
  const [selectedCopySteps, setSelectedCopySteps] = useState<string[]>([]);

  const { mutate: copySteps, isPending: isCopyStepsPending } = api.testContent.copySteps.useMutation({
    onSuccess: async () => {
      notifications.show({
        title: 'Steps copied',
        message: 'Steps copied successfully',
        color: 'green',
      });
      modals.close('copy-step-test-view');
      await trpcUtils.testContent.get.invalidate({ id: copyIntoTestContentId }).catch(() => undefined);
      await trpcUtils.testContent.getCode.invalidate({ testContentId: copyIntoTestContentId }).catch(() => undefined);
    },
    onError: (error) => {
      notifications.show({
        title: 'Error copying steps',
        message: 'Error copying steps',
        color: 'red',
      });
      console.error('Error copying steps:', error);
    },
  });

  const steps = useMemo(() => {
    return (testContent?.steps || []).filter((step) => !step.skip);
  }, [testContent?.steps]);

  const toggleStepSelection = (stepId: string) => {
    setSelectedCopySteps((prevSelected) =>
      prevSelected.includes(stepId) ? prevSelected.filter((id) => id !== stepId) : [...prevSelected, stepId],
    );
  };

  const handleCopy = () => {
    if (selectedCopySteps.length === 0) {
      return;
    }
    copySteps({
      srcTestContentId: copyFromTestContent.id,
      destTestContentId: copyIntoTestContentId,
      insertAfterStepOrder,
      stepIds: selectedCopySteps,
    });
  };

  return (
    isTestContentLoading ?
      <div className="flex h-[80vh] items-center justify-center gap-3">
        <Loader size="sm" />
        <Text
          size="md"
          c="dimmed"
        >
          Loading test...
        </Text>
      </div>
    : testContent ?
      <div className="flex h-[80vh] flex-col gap-4">
        {/* Header Section */}
        <Button
          variant="transparent"
          className="self-start"
          leftSection={<IconChevronLeft size={16} />}
          onClick={() => modals.close('copy-step-test-view')}
          aria-label="Back to Search"
        >
          Back to Search
        </Button>
        <div className="px-4">
          <CopyStepTestInfo
            testContent={copyFromTestContent}
            copyIntoTestContentId={copyIntoTestContentId}
          />
        </div>

        <Divider className="px-4" />

        {/* Controls Section */}
        <div className="px-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Checkbox
                label="Select All"
                checked={selectedCopySteps.length === steps.length && steps.length > 0}
                indeterminate={selectedCopySteps.length > 0 && selectedCopySteps.length < steps.length}
                onChange={() => {
                  if (selectedCopySteps.length === steps.length) {
                    setSelectedCopySteps([]);
                  } else {
                    setSelectedCopySteps(steps.map((step) => step.id));
                  }
                }}
              />
              <Text
                size="sm"
                c="dimmed"
                className="font-medium"
              >
                {steps.length} {steps.length === 1 ? 'step' : 'steps'} available
              </Text>
              {selectedCopySteps.length > 0 && (
                <div className="flex items-center gap-2">
                  <Text
                    size="sm"
                    c="blue"
                    className="font-medium"
                  >
                    {selectedCopySteps.length} selected
                  </Text>
                  <Tooltip label="Clear selection">
                    <ActionIcon
                      variant="transparent"
                      color="gray"
                      size="sm"
                      onClick={() => setSelectedCopySteps([])}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                </div>
              )}
            </div>
            <Button
              leftSection={<IconCopy size={16} />}
              onClick={handleCopy}
              disabled={selectedCopySteps.length === 0 || isCopyStepsPending}
              loading={isCopyStepsPending}
              className="self-start sm:self-auto"
            >
              {isCopyStepsPending ?
                'Copying...'
              : `Copy ${selectedCopySteps.length} Step${selectedCopySteps.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
        <div className="flex gap-4 px-4">
          <ScrollArea
            className="h-[calc(80vh-200px)] w-full lg:w-[500px]"
            scrollbars="y"
            type="always"
            scrollbarSize={8}
            offsetScrollbars="y"
          >
            <div className="flex flex-col gap-4">
              {steps.map((step, idx) => (
                <div
                  className="group flex items-start gap-2"
                  key={step.id}
                >
                  <Checkbox
                    className="mt-3 flex-shrink-0"
                    checked={selectedCopySteps.includes(step.id)}
                    onChange={() => toggleStepSelection(step.id)}
                  />
                  <div className="flex-1">
                    <Step
                      step={step}
                      testContentId={copyFromTestContent.id}
                      selected={selectedCopySteps.includes(step.id)}
                      readonly
                      idx={idx}
                      isFirstNavOp={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="hidden flex-1 lg:block">
            <div className="overflow-hidden rounded-lg border border-[var(--mantine-color-gray-3)] dark:border-[var(--mantine-color-dark-4)]">
              <ScrollArea
                className="h-[calc(80vh-200px)]"
                scrollbars="y"
                scrollbarSize={8}
                type="always"
              >
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-[var(--mantine-color-body)]">
                    <TableRow>
                      <TableHead className="w-12 border-b dark:border-neutral-700">Step</TableHead>
                      <TableHead className="border-b dark:border-neutral-700">Description</TableHead>
                      <TableHead className="w-12 border-b dark:border-neutral-700" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCopySteps.length > 0 ?
                      steps
                        .filter((step) => selectedCopySteps.includes(step.id))
                        .map((step) => {
                          const originalIndex = steps.findIndex((s) => s.id === step.id);
                          return (
                            <TableRow key={step.id}>
                              <TableCell className="text-center">{originalIndex + 1}</TableCell>
                              <TableCell className="break-all">{step.description}</TableCell>
                              <TableCell className="text-center">
                                <Tooltip label="Remove">
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => toggleStepSelection(step.id)}
                                  >
                                    <IconX size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    : <TableRow className="[&:not(:has(th))]:hover:bg-transparent dark:[&:not(:has(th))]:hover:bg-transparent">
                        <TableCell
                          colSpan={3}
                          className="h-24 text-center"
                        >
                          No steps selected.
                        </TableCell>
                      </TableRow>
                    }
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </div>
        </div>
      </div>
    : <div>Test not found.</div>
  );
}
